import { useState } from "react"
import { useInfiniteQuery } from "@tanstack/react-query"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@components/common/tabs"
import { useAuth } from "@stores/auth-store"
import useTranslator from "@hooks/use-translator"
import UpdateList from "../updates/updates-list"
import { getActivitiesListService } from "@pma/services/activities"

const TABS = [
  { label: "All", value: "all" },
  { label: "Mentioned", value: "mentioned" },
  { label: "Assigned", value: "assigned" },
] as const

type TabValue = (typeof TABS)[number]["value"]

export const UpdatesTabs = () => {
  const { authConfig } = useAuth()
  const orgId = authConfig?.last_organization_id
  const { translate } = useTranslator()
  const [activeTab, setActiveTab] = useState<TabValue>("all")

  const { data, refetch, isLoading, fetchNextPage, hasNextPage } = useInfiniteQuery({
    queryKey: ["log-notifications", activeTab],
    queryFn: ({ pageParam = 1 }) =>
      getActivitiesListService({
        type: activeTab,
        paging_per_page: 30,
        paging_start_page: pageParam,
        organization_id: orgId,
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage?.nextPage) {
        return undefined
      }
      return allPages.length + 1
    },
  })

  const handleTabChange = (value: string) => {
    setActiveTab(value as TabValue)
  }

  return (
    <Tabs value={activeTab} onValueChange={handleTabChange} className='w-full bg-none'>
      <TabsList className='grid w-full grid-cols-3 justify-evenly gap-1 rounded-full bg-slate-200'>
        {TABS.map((tab) => (
          <TabsTrigger
            key={tab.value}
            value={tab.value}
            className='rounded-full px-1 text-[8.8px] text-xs sm:px-2 sm:text-[10px] md:px-3 md:text-[9px]'
          >
            {translate(tab.label)}
          </TabsTrigger>
        ))}
      </TabsList>

      {TABS.map((tab) => (
        <TabsContent key={tab.value} value={tab.value}>
          <UpdateList
            data={data}
            onNotificationClick={refetch}
            loadMore={hasNextPage ? fetchNextPage : undefined}
            context='logs'
            isLoading={isLoading}
          />
        </TabsContent>
      ))}
    </Tabs>
  )
}
